/**
 * Unified app UI design-system class tokens.
 * Source of truth traits from Connectivité / Hotels list / Circuits switchers.
 * Do not invent new looks — reuse these across admin / partner / affiliate
 * (except product moteurs).
 */

export const dsFocus =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)]/40 focus-visible:ring-offset-2'

export const ds = {
  /** §6 — typography baseline (Liste hotels scale) */
  text: 'text-sm font-[family-name:var(--font-sans)] text-[var(--foreground)]',
  textMuted: 'text-sm font-[family-name:var(--font-sans)] text-[var(--muted-foreground)]',
  panelTitle: 'text-sm font-semibold text-[var(--foreground)]',
  caption: 'text-xs leading-4 text-[var(--muted-foreground)]',

  /** §8 — page shell (no nested gray slabs) */
  pageShell: 'min-h-0 bg-transparent',
  pageStack: 'flex flex-col gap-5 sm:gap-6',

  /** §1 — soft primary button (Gérer les destinations) */
  btnSoft: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] border border-[var(--color-primary-200)]',
    'bg-[var(--color-primary-50)] px-4 text-sm font-semibold',
    'text-[var(--color-primary-700)]',
    'motion-safe:transition-all motion-safe:duration-200',
    'hover:border-[var(--color-primary-300)] hover:bg-[var(--color-primary-100)]',
    'active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-50',
    dsFocus,
  ].join(' '),

  btnSolid: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] border border-transparent',
    'bg-[var(--color-primary-600)] px-4 text-sm font-semibold text-white',
    'shadow-[var(--shadow-xs)]',
    'motion-safe:transition-all motion-safe:duration-200',
    'hover:bg-[var(--color-primary-700)]',
    'active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-50',
    dsFocus,
  ].join(' '),

  btnSecondary: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] border border-[var(--border)]',
    'bg-[var(--card)] px-4 text-sm font-semibold text-[var(--foreground)]',
    'motion-safe:transition-all motion-safe:duration-200',
    'hover:bg-[var(--muted)]',
    'active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-50',
    dsFocus,
  ].join(' '),

  btnDanger: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] border border-[var(--error-border)]',
    'bg-[var(--error-bg)] px-4 text-sm font-semibold text-[var(--error-ink)]',
    'motion-safe:transition-all motion-safe:duration-200',
    'hover:bg-[color-mix(in_oklab,var(--error-bg)_80%,var(--error-color)_20%)]',
    'active:scale-[0.98]',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--error-color)]/40 focus-visible:ring-offset-2',
  ].join(' '),

  btnGhost: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] px-3 text-sm font-medium text-[var(--muted-foreground)]',
    'motion-safe:transition-colors motion-safe:duration-200',
    'hover:bg-[var(--muted)] hover:text-[var(--foreground)]',
    'disabled:pointer-events-none disabled:opacity-50',
    dsFocus,
  ].join(' '),

  /** Single-row “Details” action (Unconfigured entities) */
  btnDetails: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] px-3 text-sm font-medium',
    'text-[var(--color-primary-700)]',
    'motion-safe:transition-colors motion-safe:duration-200',
    'hover:bg-[var(--color-primary-50)]',
    'disabled:pointer-events-none disabled:opacity-50',
    dsFocus,
  ].join(' '),

  /** Labeled destructive twin of details (same chrome, error colors) */
  btnDetailsDanger: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] px-3 text-sm font-medium',
    'text-[var(--error-ink)]',
    'motion-safe:transition-colors motion-safe:duration-200',
    'hover:bg-[var(--error-bg)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--error-color)]/40 focus-visible:ring-offset-2',
  ].join(' '),

  btnIcon: [
    'inline-flex h-9 max-h-9 min-w-9 items-center justify-center',
    'rounded-[var(--radius-sm)] border border-[var(--color-primary-200)]',
    'bg-[var(--color-primary-50)] text-[var(--color-primary-700)]',
    'motion-safe:transition-all motion-safe:duration-200',
    'hover:border-[var(--color-primary-300)] hover:bg-[var(--color-primary-100)]',
    'disabled:pointer-events-none disabled:opacity-50',
    dsFocus,
  ].join(' '),

  btnIconDanger: [
    'inline-flex h-9 max-h-9 min-w-9 items-center justify-center',
    'rounded-[var(--radius-sm)] border border-[var(--error-border)]',
    'bg-[var(--error-bg)] text-[var(--error-ink)]',
    'motion-safe:transition-all motion-safe:duration-200',
    'hover:bg-[color-mix(in_oklab,var(--error-bg)_80%,var(--error-color)_20%)]',
    'disabled:pointer-events-none disabled:opacity-50',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--error-color)]/40 focus-visible:ring-offset-2',
  ].join(' '),

  /** §2 — section / config card (ConfigSectionCard) */
  sectionCard: [
    'overflow-hidden rounded-[var(--radius-md)]',
    'border border-[var(--border)] bg-[var(--card)]',
    'shadow-[var(--shadow-sm)]',
  ].join(' '),

  sectionCardHeader: [
    'flex flex-wrap items-start justify-between gap-4 border-b',
    'border-[var(--border)] px-5 py-4 sm:px-6',
    'bg-gradient-to-r from-[var(--muted)] via-[var(--card)] to-[var(--muted)]',
  ].join(' '),

  sectionCardHeaderPrimary: [
    'flex flex-wrap items-start justify-between gap-4 border-b',
    'border-[var(--color-primary-100)] px-5 py-4 sm:px-6',
    'bg-gradient-to-r from-[var(--color-primary-50)] via-[var(--card)] to-[var(--color-primary-50)]/40',
  ].join(' '),

  sectionCardBody:
    'bg-[var(--card)] p-5 sm:p-6',

  /** §3 — segmented filter tabs / status switchers */
  switcherTrack: [
    'inline-flex w-full max-w-full flex-wrap gap-2.5',
    'bg-transparent p-0 border-0',
  ].join(' '),

  /** Unified switcher track (one control, not separate buttons) */
  switcherTrackSolid: [
    'inline-flex h-9 max-h-9 w-fit max-w-full items-stretch gap-1',
    'rounded-[var(--radius-sm)] border border-[var(--border)]',
    'bg-[var(--muted)] p-0.5',
  ].join(' '),

  switcherOption: [
    'inline-flex h-9 max-h-9 items-center justify-center gap-2',
    'rounded-[var(--radius-sm)] px-4 text-sm font-semibold',
    'motion-safe:transition-all motion-safe:duration-200',
    'disabled:pointer-events-none disabled:opacity-50',
    dsFocus,
  ].join(' '),

  /** Filter tab active — SoftButton soft (standalone pills) */
  switcherOptionActive: [
    'border border-[var(--color-primary-200)]',
    'bg-[var(--color-primary-50)] text-[var(--color-primary-700)]',
    'hover:border-[var(--color-primary-300)] hover:bg-[var(--color-primary-100)]',
  ].join(' '),

  switcherOptionInactive: [
    'border border-[var(--border)]',
    'bg-[var(--card)] text-[var(--muted-foreground)]',
    'hover:bg-[var(--muted)] hover:text-[var(--foreground)]',
  ].join(' '),

  /** Status switcher — thumb inside track */
  switcherOptionActiveSolid: [
    'min-w-[5.25rem] border border-transparent',
    'bg-[var(--card)] text-[var(--color-primary-700)]',
    'shadow-[var(--shadow-xs)]',
  ].join(' '),

  switcherOptionInactiveSolid: [
    'min-w-[5.25rem] border border-transparent',
    'bg-transparent text-[var(--muted-foreground)]',
    'hover:text-[var(--foreground)]',
  ].join(' '),

  /** §4 — data table shell (Liste hotels) */
  tableWrap: [
    'overflow-hidden rounded-[var(--radius-lg)]',
    'border border-[var(--border)] bg-[var(--card)]',
    'shadow-[var(--shadow-sm)]',
  ].join(' '),

  table: 'min-w-full text-xs font-[family-name:var(--font-sans)]',
  tableHead: 'border-b border-[var(--border)]',
  tableTh:
    'h-9 px-2 text-left align-middle text-xs font-medium text-[var(--muted-foreground)]',
  tableRow:
    'border-b border-[var(--border)] text-[var(--foreground)] transition-colors hover:bg-[color-mix(in_oklab,var(--muted)_50%,transparent)]',
  tableTd: 'p-1.5 align-middle text-xs',
  tableFooter:
    'flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border)] px-3 py-2 text-xs',

  /** §5 — row action group */
  rowActions: 'flex flex-wrap items-center justify-end gap-1.5',

  /** §7 — tabs container */
  tabsNav: [
    'rounded-2xl border border-[var(--border)] bg-[var(--card)] p-2',
    'shadow-[var(--shadow-sm)]',
  ].join(' '),

  tab: [
    'inline-flex min-h-11 shrink-0 items-center gap-2 rounded-xl px-3.5',
    'text-sm font-medium transition',
    dsFocus,
  ].join(' '),

  tabActive: 'bg-[var(--color-primary-500)] text-white shadow-sm',
  tabInactive: 'text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)]',

  /** §9 — profile / header utility trigger */
  profileTrigger: [
    'inline-flex min-h-11 min-w-11 items-center justify-center',
    'rounded-[var(--radius-sm)] border border-[var(--color-primary-200)]',
    'bg-[var(--color-primary-50)] text-[var(--color-primary-700)]',
    'motion-safe:transition-all motion-safe:duration-200',
    'hover:border-[var(--color-primary-300)] hover:bg-[var(--color-primary-100)]',
    dsFocus,
  ].join(' '),

  profileMenu: [
    'min-w-[17.5rem] overflow-hidden rounded-[var(--radius-md)]',
    'border border-[var(--border)] bg-[var(--card)]',
    'shadow-[var(--shadow-md)]',
  ].join(' '),

  /** Overflow / popover panel (tabs ⋯) */
  overflowPanel: [
    'z-50 min-w-[12rem] overflow-hidden rounded-[var(--radius-md)]',
    'border border-[var(--border)] bg-[var(--card)] p-1',
    'shadow-[var(--shadow-md)]',
  ].join(' '),

  /**
   * §10 — Form fields (Travelgate Configuration generale → Connexion API)
   * Reference: GeneralConfigTable inputs / labels / hints
   */
  fieldLabel: 'mb-2 block text-sm font-semibold text-[var(--foreground)]',
  fieldHint: 'mt-1.5 text-xs leading-4 text-[var(--muted-foreground)]',
  fieldError: 'mt-1.5 text-xs font-medium text-[var(--error-ink)]',
  fieldGroup: [
    'rounded-[var(--radius-sm)] border border-[var(--color-primary-100)]',
    'bg-gradient-to-br from-[var(--color-primary-50)]/80 via-[var(--card)] to-[var(--muted)]',
    'p-4 sm:p-5',
  ].join(' '),
  fieldGroupTitle:
    'mb-4 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-primary-700)]',
  input: [
    'h-11 w-full rounded-[var(--radius-sm)] border border-[var(--border-strong)]',
    'bg-[var(--card)] px-3 text-sm text-[var(--foreground)]',
    'placeholder:text-[var(--muted-foreground)]',
    'focus:border-[var(--primary)] focus:outline-none',
    'focus:ring-2 focus:ring-[var(--primary)]/25',
    'disabled:cursor-not-allowed disabled:bg-[var(--muted)] disabled:text-[var(--muted-foreground)]',
  ].join(' '),
  inputSm: [
    'h-10 w-full rounded-[var(--radius-sm)] border border-[var(--border-strong)]',
    'bg-[var(--card)] px-3 text-sm text-[var(--foreground)]',
    'placeholder:text-[var(--muted-foreground)]',
    'focus:border-[var(--primary)] focus:outline-none',
    'focus:ring-2 focus:ring-[var(--primary)]/25',
    'disabled:cursor-not-allowed disabled:bg-[var(--muted)] disabled:text-[var(--muted-foreground)]',
  ].join(' '),
  select: [
    'h-11 w-full cursor-pointer rounded-[var(--radius-sm)] border border-[var(--border-strong)]',
    'bg-[var(--card)] px-3 text-sm text-[var(--foreground)]',
    'focus:border-[var(--primary)] focus:outline-none',
    'focus:ring-2 focus:ring-[var(--primary)]/25',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ].join(' '),
  /** Dense filters / compact rows — keep default `select` (h-11) elsewhere. */
  selectSm: [
    'h-10 w-full cursor-pointer rounded-[var(--radius-sm)] border border-[var(--border-strong)]',
    'bg-[var(--card)] px-3 text-sm text-[var(--foreground)]',
    'focus:border-[var(--primary)] focus:outline-none',
    'focus:ring-2 focus:ring-[var(--primary)]/25',
    'disabled:cursor-not-allowed disabled:opacity-50',
  ].join(' '),
  textarea: [
    'w-full rounded-[var(--radius-sm)] border border-[var(--border-strong)]',
    'bg-[var(--card)] px-3 py-2 text-sm text-[var(--foreground)]',
    'placeholder:text-[var(--muted-foreground)]',
    'focus:border-[var(--primary)] focus:outline-none',
    'focus:ring-2 focus:ring-[var(--primary)]/25',
    'disabled:cursor-not-allowed disabled:bg-[var(--muted)]',
  ].join(' '),
  inputError:
    'border-[var(--error-color)] focus:border-[var(--error-color)] focus:ring-[var(--error-bg)]',
  inputAffixBtn: [
    'absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center',
    'rounded-[var(--radius-sm)] text-[var(--muted-foreground)]',
    'hover:bg-[var(--muted)] hover:text-[var(--foreground)]',
    dsFocus,
  ].join(' '),

  /**
   * §11 — Badges (Travelgate brand / status pills)
   * Reference: HS.badgeBrand, status pills on providers / hotels
   */
  badge: 'inline-flex w-fit max-w-full items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
  badgeBrand: [
    'inline-flex w-fit max-w-full items-center rounded-full bg-[var(--color-primary-50)] px-3 py-1',
    'text-xs font-semibold uppercase tracking-wide text-[var(--color-primary-700)]',
    'ring-1 ring-inset ring-[var(--color-primary-200)]',
  ].join(' '),
  badgeSuccess:
    'inline-flex w-fit max-w-full items-center rounded-full bg-[var(--success-green-bg)] px-2.5 py-0.5 text-xs font-semibold text-[var(--success-green-ink)]',
  badgeDanger:
    'inline-flex w-fit max-w-full items-center rounded-full bg-[var(--error-bg)] px-2.5 py-0.5 text-xs font-semibold text-[var(--error-ink)]',
  badgeWarning:
    'inline-flex w-fit max-w-full items-center rounded-full bg-[var(--warning-bg)] px-2.5 py-0.5 text-xs font-semibold text-[var(--warning-ink)]',
  badgeNeutral: [
    'inline-flex w-fit max-w-full items-center rounded-full border border-[var(--border)]',
    'bg-[var(--muted)] px-2.5 py-0.5 text-xs font-semibold text-[var(--foreground)]',
  ].join(' '),

  /**
   * §12 — Puces / chips (filter & amenity chips from Travelgate)
   * Reference: HS.filterChip, HS.amenityChip, boardBadge
   */
  chip: [
    'inline-flex items-center gap-1.5 rounded-full border border-[var(--color-primary-200)]',
    'bg-[var(--color-primary-50)] px-3 py-1.5 text-sm font-medium text-[var(--color-primary-700)]',
  ].join(' '),
  chipMuted: [
    'inline-flex items-center gap-1 rounded-[var(--radius-xs)]',
    'bg-[var(--muted)] px-2 py-1 text-xs text-[var(--foreground)]',
  ].join(' '),
  chipRemovable: [
    'inline-flex items-center gap-1.5 rounded-full border border-[var(--color-primary-200)]',
    'bg-[var(--color-primary-50)] px-2.5 py-1 text-sm font-medium text-[var(--color-primary-700)]',
  ].join(' '),
} as const

export type DsTokenKey = keyof typeof ds

/** Build input class with optional error + size (Travelgate buildInputClass parity). */
export const buildInputClass = (
  hasError = false,
  size: 'md' | 'sm' = 'md'
) => {
  const base = size === 'sm' ? ds.inputSm : ds.input
  return hasError ? `${base} ${ds.inputError}` : base
}

/** Build select class; default `md` preserves existing h-11 everywhere. */
export const buildSelectClass = (
  hasError = false,
  size: 'md' | 'sm' = 'md'
) => {
  const base = size === 'sm' ? ds.selectSm : ds.select
  return hasError ? `${base} ${ds.inputError}` : base
}
